import { useState, useEffect, useMemo } from 'react';
import {
  ShieldAlert,
  Search,
  Trash2,
  RefreshCw,
  AlertCircle,
  UserX,
  ChevronDown,
  ChevronUp,
  X,
  Loader2,
} from 'lucide-react';
import { collection, getDocs, query } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Organization, OrganizationMember } from '../../types/index';
import { useAuthReady } from '../../hooks/useAuthReady';
import {
  adminDeleteOrganization,
  adminDeleteUsers,
  fetchAdminOrgDeletionImpact,
  AdminOrgMemberImpact,
} from '../../utils/organizationAdminApi';

type OrgRow = Organization & { creatorEmail?: string };

type ConfirmModalState =
  | {
      type: 'org';
      org: OrgRow;
      selectedMembers: AdminOrgMemberImpact[];
    }
  | {
      type: 'users';
      selectedMembers: AdminOrgMemberImpact[];
      currentOrgName: string;
    }
  | null;

export function OrganizationAdmin() {
  const authReady = useAuthReady(true);
  const [organizations, setOrganizations] = useState<OrgRow[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [impactMembers, setImpactMembers] = useState<AdminOrgMemberImpact[]>([]);
  const [impactLoading, setImpactLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmName, setConfirmName] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  const loadOrganizations = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const snapshot = await getDocs(query(collection(db, 'organizations')));
      const orgs = snapshot.docs.map((docSnap) => {
        const data = docSnap.data() as Organization;
        const creator = (data.members || []).find(
          (m: OrganizationMember) => m.userId === data.creatorId
        );
        return {
          ...data,
          id: docSnap.id,
          creatorEmail: creator?.email,
        } as OrgRow;
      });
      orgs.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
      setOrganizations(orgs);
    } catch (err) {
      console.error(err);
      setError('Impossible de charger les organisations.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!authReady) return;
    loadOrganizations();
  }, [authReady]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return organizations;
    return organizations.filter(
      (org) =>
        org.name?.toLowerCase().includes(q) ||
        org.id?.toLowerCase().includes(q) ||
        org.creatorEmail?.toLowerCase().includes(q) ||
        (org.members || []).some(
          (m) =>
            m.email?.toLowerCase().includes(q) || m.name?.toLowerCase().includes(q)
        )
    );
  }, [organizations, searchTerm]);

  const selectedMembers = useMemo(
    () => impactMembers.filter((m) => selectedIds.has(m.userId)),
    [impactMembers, selectedIds]
  );

  const openImpact = async (orgId: string) => {
    if (expandedId === orgId) {
      setExpandedId(null);
      setImpactMembers([]);
      setSelectedIds(new Set());
      setConfirmName('');
      return;
    }
    setExpandedId(orgId);
    setConfirmName('');
    setSelectedIds(new Set());
    setSuccess(null);
    setError(null);
    setImpactLoading(true);
    try {
      const res = await fetchAdminOrgDeletionImpact(orgId);
      setImpactMembers(res.members || []);
    } catch (err: any) {
      setError(err?.message || 'Erreur lors du chargement des membres.');
      setImpactMembers([]);
    } finally {
      setImpactLoading(false);
    }
  };

  const toggleMember = (userId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(impactMembers.map((m) => m.userId)));
  const selectNone = () => setSelectedIds(new Set());
  const selectUnverified = () =>
    setSelectedIds(new Set(impactMembers.filter((m) => !m.emailVerified).map((m) => m.userId)));
  const selectNoOtherOrgs = () =>
    setSelectedIds(new Set(impactMembers.filter((m) => m.otherOrgCount === 0).map((m) => m.userId)));
  const selectSpamPreset = () =>
    setSelectedIds(
      new Set(impactMembers.filter((m) => m.suggestedForSpamCleanup).map((m) => m.userId))
    );

  const requestDeleteOrg = (org: OrgRow) => {
    if (confirmName.trim() !== String(org.name || '').trim()) {
      setError('Tapez le nom exact de l’organisation pour confirmer.');
      return;
    }
    setConfirmModal({
      type: 'org',
      org,
      selectedMembers,
    });
  };

  const requestDeleteUsers = (org: OrgRow) => {
    if (selectedMembers.length === 0) {
      setError('Cochez au moins un compte à supprimer.');
      return;
    }
    setConfirmModal({
      type: 'users',
      selectedMembers,
      currentOrgName: org.name,
    });
  };

  const executeConfirmedAction = async () => {
    if (!confirmModal) return;
    setConfirmSubmitting(true);
    setBusy(true);
    setError(null);
    setSuccess(null);

    try {
      if (confirmModal.type === 'org') {
        const { org, selectedMembers: membersToDelete } = confirmModal;
        const res = await adminDeleteOrganization({
          organizationId: org.id,
          confirmationName: confirmName,
          userIdsToDelete: membersToDelete.map((m) => m.userId),
        });
        const deletedUsers = (res.result as any)?.deletedUsers?.length ?? 0;
        setSuccess(
          `Organisation « ${org.name} » supprimée · ${deletedUsers} compte(s) supprimé(s).`
        );
        setExpandedId(null);
        setConfirmName('');
        setSelectedIds(new Set());
        await loadOrganizations();
      } else {
        const ids = confirmModal.selectedMembers.map((m) => m.userId);
        const res = await adminDeleteUsers({ userIds: ids });
        const deletedUsers = (res.result as any)?.deletedUsers?.length ?? 0;
        const deletedOrgs = (res.result as any)?.deletedOrganizations?.length ?? 0;
        setSuccess(
          `${deletedUsers} compte(s) supprimé(s)${
            deletedOrgs > 0
              ? ` · ${deletedOrgs} organisation(s) devenue(s) vide(s) aussi supprimée(s)`
              : ''
          }.`
        );
        setSelectedIds(new Set());
        if (expandedId) {
          try {
            const impact = await fetchAdminOrgDeletionImpact(expandedId);
            setImpactMembers(impact.members || []);
          } catch {
            setExpandedId(null);
            setImpactMembers([]);
          }
        }
        await loadOrganizations();
      }
      setConfirmModal(null);
    } catch (err: any) {
      setError(err?.message || 'Échec de la suppression.');
      setConfirmModal(null);
    } finally {
      setConfirmSubmitting(false);
      setBusy(false);
    }
  };

  if (!authReady || isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-menioo-green" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center space-x-3">
            <ShieldAlert className="h-6 w-6 text-menioo-green" />
            <h2 className="text-xl font-display font-bold text-menioo-navy">
              Organisation Admin
            </h2>
          </div>
          <p className="mt-1 text-sm text-gray-600 max-w-2xl">
            Cochez les comptes à traiter, puis choisissez de supprimer l’organisation
            et/ou uniquement les comptes sélectionnés.
          </p>
        </div>
        <button
          type="button"
          onClick={loadOrganizations}
          className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-white text-menioo-navy"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Rafraîchir
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md text-sm flex items-start gap-2">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="bg-menioo-green/10 border border-menioo-green/30 text-menioo-navy px-4 py-3 rounded-md text-sm">
          {success}
        </div>
      )}

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="search"
          placeholder="Rechercher nom, email, id…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-menioo-green focus:border-menioo-green"
        />
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Organisation
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Créateur
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Membres
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Créée le
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filtered.map((org) => {
              const isExpanded = expandedId === org.id;
              return (
                <tr key={org.id} className="align-top">
                  <td className="px-4 py-3" colSpan={isExpanded ? 5 : 1}>
                    {!isExpanded ? (
                      <div>
                        <div className="text-sm font-medium text-menioo-navy">{org.name}</div>
                        <div className="text-xs text-gray-500 font-mono">{org.id}</div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="text-sm font-medium text-menioo-navy">{org.name}</div>
                            <div className="text-xs text-gray-500 font-mono">{org.id}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => openImpact(org.id)}
                            className="text-sm text-gray-500 hover:text-menioo-navy inline-flex items-center"
                          >
                            Fermer <ChevronUp className="h-4 w-4 ml-1" />
                          </button>
                        </div>

                        {impactLoading ? (
                          <p className="text-sm text-gray-500">Chargement des membres…</p>
                        ) : (
                          <>
                            <div className="rounded-md bg-menioo-green/10 border border-menioo-green/30 p-3 text-sm text-menioo-navy flex gap-2">
                              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-menioo-green" />
                              <span>
                                Cochez les comptes concernés. Ensuite vous pouvez supprimer
                                l’organisation (avec les comptes cochés) ou seulement les
                                comptes cochés (l’organisation reste si d’autres membres
                                subsistent).
                              </span>
                            </div>

                            <div className="flex flex-wrap gap-2 text-xs">
                              <button type="button" onClick={selectAll} className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 text-menioo-navy">
                                Tout cocher
                              </button>
                              <button type="button" onClick={selectNone} className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 text-menioo-navy">
                                Tout décocher
                              </button>
                              <button type="button" onClick={selectUnverified} className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 text-menioo-navy">
                                Non vérifiés
                              </button>
                              <button type="button" onClick={selectNoOtherOrgs} className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 text-menioo-navy">
                                Sans autre org
                              </button>
                              <button type="button" onClick={selectSpamPreset} className="px-2 py-1 rounded border border-menioo-green/40 bg-menioo-green/10 hover:bg-menioo-green/20 text-menioo-navy">
                                Non vérifiés + sans autre org
                              </button>
                            </div>

                            <div className="overflow-x-auto border border-gray-100 rounded-md">
                              <table className="min-w-full text-sm">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-3 py-2 text-left w-10" />
                                    <th className="px-3 py-2 text-left text-gray-500 font-medium">Email</th>
                                    <th className="px-3 py-2 text-left text-gray-500 font-medium">Rôle</th>
                                    <th className="px-3 py-2 text-left text-gray-500 font-medium">Email vérifié</th>
                                    <th className="px-3 py-2 text-left text-gray-500 font-medium">Autres orgs</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {impactMembers.map((m) => (
                                    <tr
                                      key={m.userId}
                                      className={selectedIds.has(m.userId) ? 'bg-menioo-green/5' : undefined}
                                    >
                                      <td className="px-3 py-2">
                                        <input
                                          type="checkbox"
                                          className="rounded border-gray-300 text-menioo-green focus:ring-menioo-green"
                                          checked={selectedIds.has(m.userId)}
                                          onChange={() => toggleMember(m.userId)}
                                        />
                                      </td>
                                      <td className="px-3 py-2 text-menioo-navy">{m.email || m.userId}</td>
                                      <td className="px-3 py-2">{m.role}</td>
                                      <td className="px-3 py-2">{m.emailVerified ? 'Oui' : 'Non'}</td>
                                      <td className="px-3 py-2">{m.otherOrgCount}</td>
                                    </tr>
                                  ))}
                                  {impactMembers.length === 0 && (
                                    <tr>
                                      <td colSpan={5} className="px-3 py-3 text-gray-500">
                                        Aucun membre
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>

                            <p className="text-xs text-gray-500">
                              {selectedIds.size} compte(s) sélectionné(s)
                            </p>

                            <div className="flex flex-wrap items-end gap-3">
                              <div className="flex-1 min-w-[200px]">
                                <label className="block text-xs text-gray-500 mb-1">
                                  Pour supprimer l’org — tapez :{' '}
                                  <strong className="text-menioo-navy">{org.name}</strong>
                                </label>
                                <input
                                  type="text"
                                  value={confirmName}
                                  onChange={(e) => setConfirmName(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-menioo-green focus:border-menioo-green"
                                  placeholder={org.name}
                                />
                              </div>
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => requestDeleteOrg(org)}
                                className="inline-flex items-center px-4 py-2 bg-menioo-green text-white text-sm font-medium rounded-md hover:bg-menioo-green-dark focus:outline-none focus:ring-2 focus:ring-menioo-green focus:ring-offset-2 disabled:opacity-50"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Supprimer l’organisation
                              </button>
                              <button
                                type="button"
                                disabled={busy || selectedIds.size === 0}
                                onClick={() => requestDeleteUsers(org)}
                                className="inline-flex items-center px-4 py-2 bg-white text-menioo-navy text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                              >
                                <UserX className="h-4 w-4 mr-2" />
                                Supprimer les comptes cochés
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                  {!isExpanded && (
                    <>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {org.creatorEmail || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {(org.members || []).length}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {org.createdAt
                          ? new Date(org.createdAt).toLocaleString()
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => openImpact(org.id)}
                          className="inline-flex items-center text-menioo-green hover:text-menioo-green-dark text-sm font-medium"
                        >
                          Gérer
                          <ChevronDown className="h-4 w-4 ml-1" />
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="p-8 text-center text-gray-500 text-sm">Aucune organisation</div>
        )}
      </div>

      {confirmModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="org-admin-confirm-title"
            className="bg-white rounded-lg shadow-xl max-w-lg w-full"
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-menioo-green" />
                <h3
                  id="org-admin-confirm-title"
                  className="text-lg font-display font-semibold text-menioo-navy"
                >
                  {confirmModal.type === 'org'
                    ? 'Confirmer la suppression de l’organisation'
                    : 'Confirmer la suppression des comptes'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => !confirmSubmitting && setConfirmModal(null)}
                disabled={confirmSubmitting}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 space-y-3 text-sm text-menioo-navy">
              {confirmModal.type === 'org' ? (
                <>
                  <p>
                    Organisation <strong>« {confirmModal.org.name} »</strong> :{' '}
                    <span className="text-menioo-green font-medium">sera supprimée</span>
                  </p>
                  <div>
                    <p className="font-medium mb-1">
                      Comptes à supprimer ({confirmModal.selectedMembers.length})
                    </p>
                    {confirmModal.selectedMembers.length === 0 ? (
                      <p className="text-gray-500">Aucun — seuls les comptes non cochés resteront.</p>
                    ) : (
                      <ul className="list-disc pl-5 space-y-0.5 text-gray-700">
                        {confirmModal.selectedMembers.map((m) => (
                          <li key={m.userId}>
                            {m.email || m.userId}
                            {m.emailVerified ? ' (vérifié)' : ' (non vérifié)'}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {impactMembers.length > confirmModal.selectedMembers.length && (
                    <p className="text-gray-500">
                      {impactMembers.length - confirmModal.selectedMembers.length} compte(s)
                      conservé(s) (retirés de cette org uniquement).
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p>
                    Organisation <strong>« {confirmModal.currentOrgName} »</strong> :{' '}
                    <span className="font-medium">non ciblée</span>
                    {' '}(supprimée seulement si elle se retrouve sans membre).
                  </p>
                  <div>
                    <p className="font-medium mb-1">
                      Comptes à supprimer ({confirmModal.selectedMembers.length})
                    </p>
                    <ul className="list-disc pl-5 space-y-0.5 text-gray-700">
                      {confirmModal.selectedMembers.map((m) => (
                        <li key={m.userId}>
                          {m.email || m.userId}
                          {m.emailVerified ? ' (vérifié)' : ' (non vérifié)'}
                          {m.otherOrgCount > 0
                            ? ` · ${m.otherOrgCount} autre(s) org`
                            : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
              <p className="text-xs text-gray-500">Cette action est définitive.</p>
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setConfirmModal(null)}
                disabled={confirmSubmitting}
                className="px-4 py-2 text-sm font-medium text-menioo-navy bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={executeConfirmedAction}
                disabled={confirmSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-menioo-green rounded-md hover:bg-menioo-green-dark disabled:opacity-50 flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-menioo-green focus:ring-offset-2"
              >
                {confirmSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {confirmSubmitting ? 'Suppression…' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
